import { defineComponent as _defineComponent } from 'vue'
import { withKeys as _withKeys, unref as _unref, createElementVNode as _createElementVNode, withDirectives as _withDirectives, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, normalizeClass as _normalizeClass, renderList as _renderList, Fragment as _Fragment, renderSlot as _renderSlot, toDisplayString as _toDisplayString, vShow as _vShow } from "vue"

const _hoisted_1 = { class: "input__wrapper" }
const _hoisted_2 = ["type", "value", "name", "required", "autocomplete", "readonly", "step", "min", "max", "placeholder", "tabIndex", "disabled", "onKeydown", "onKeyup"]
const _hoisted_3 = ["for", "innerHTML"]
const _hoisted_4 = {
  key: 1,
  class: "input__icon-wrap"
}
const _hoisted_5 = {
  key: 2,
  class: "input__icon-wrap"
}
const _hoisted_6 = {
  key: 3,
  class: "input__icon-wrap"
}
const _hoisted_7 = {
  role: "menu",
  class: "input__search-results"
}
const _hoisted_8 = { class: "search-results__title" }
const _hoisted_9 = { class: "search-results__subtitle" }
const _hoisted_10 = {
  key: 0,
  class: "input__hint"
}
const _hoisted_11 = {
  key: 1,
  class: "input__error"
}

import { vMaska, MaskOptions, Mask } from 'maska';
import { IInputProps } from '@/@types/components/ui/forms'

import {
  withDefaults,
  computed,
  getCurrentInstance,
  ref,
} from 'vue';
import { vOnClickOutside } from '@vueuse/components';

import SmIcon from '@/components/ui/SmIcon/SmIcon.vue';

import '@/components/ui/form/SmInput/style.scss';

// [START] Props

export default /*#__PURE__*/_defineComponent({
  __name: 'SmInput',
  props: {
    modelValue: { default: '' },
    label: {},
    type: { default: 'text' },
    name: {},
    required: { type: Boolean, default: false },
    autocomplete: { default: 'off' },
    readonly: { type: Boolean, default: false },
    step: {},
    min: {},
    max: {},
    placeholder: {},
    showCancel: { type: Boolean, default: false },
    id: {},
    tabIndex: { default: 0 },
    hint: {},
    error: {},
    mega: { type: Boolean, default: false },
    search: { type: Boolean, default: false },
    searchResults: { default: () => [] },
    showResultsByInput: { type: Boolean, default: false },
    resultsMax: {},
    maskOptions: {},
    disabled: { type: Boolean }
  },
  emits: ["update:modelValue", "search"],
  setup(__props: any, { emit: emits }) {

const props = __props;


// [END] Props

// [START] Emits

// [END] Emits

// [START] Variables
const instance = getCurrentInstance();
const uid = ref<number | undefined>(instance?.uid);
const root = ref<HTMLElement | null>(null);
const input = ref<HTMLElement | null>(null);
const showSearchResults = ref<boolean>(false);
const searchResultsElement = ref<HTMLElement | null>(null);
const passwordInputType = ref<string>('password');

// [START] Computed
const vMaskaOptions = computed((): MaskOptions => {
  if (props.type === 'phone') {
    return {
      mask: '+7 ### ###-##-##'
    }
  }

  return props.maskOptions
});

const mask = computed(() => (
  new Mask(vMaskaOptions.value)
))

const inputValue = computed((): string | number => {
  if (!props.maskOptions && props.type !== 'phone') return props.modelValue;

  return (String(props.modelValue) !== '')
    ? mask.value.masked(String(props.modelValue))
    : String(props.modelValue)
});

const classList = computed(() => {
  return [
    props.modelValue && 'input--contented',
    props.error?.length && 'input--error',
    props.mega && 'input--mega',
    showSearchResults.value && 'input--results',
  ]
    .filter(Boolean)
    .join(' ');
});

const shouldShowCancelIcon = computed(
  () => (props.showCancel || props.search) && props.modelValue,
);
const shouldShowSearchIcon = computed(
  () => showSearchResults.value && props.searchResults,
);
const shouldShowHint = computed(() => props.hint && !props.error);
const id = computed(() => props.id || `input-${uid.value}`);
const name = computed(() => props.name || `input-${uid.value}`);
const nativeType = computed(() => {
  switch (props.type) {
    case 'phone':
      return 'text';
    case 'password':
      return passwordInputType.value;
    default:
      return props.type;
  }
});
const passwordIconName = computed(() => {
  return passwordInputType.value === 'password' ? 'eye' : 'eyeOff';
});

const passwordIconClassList = computed(() => {
  return inputValue.value && 'active';
});
// [END] Computed

// [START] Methods
const handleFocus = (): void => {
  input.value?.focus();
};

const handleCancel = (): void => {
  const target = (input.value as HTMLInputElement);
  if (!target) return;
  target.value = '';
  target.dispatchEvent(new Event('input', { bubbles: true }));
};

const handleInput = ($event: InputEvent) => {
  const targetValue = ($event.target as HTMLInputElement).value;
  let result: string | number;

  // // Если не задана маска или тип поля !== phone, передать нативное значение инпута
  if (!props.maskOptions || props.type !== 'phone') {
    result = targetValue;
  }

  // В противном случае передать значение unmasked
  result =
    props.type === 'phone'
      ? `7${mask.value.unmasked(targetValue)}`
      : mask.value.unmasked(targetValue);

  emits('update:modelValue', result);

  if (props.showResultsByInput) {
    emits('search', result);
  }
};

const handleSearch = (): void => {
  showSearchResults.value = true;
  handleFocusSearchResults();

  // TODO: Нужно как-то получить unmasked, если не обходимо
  // emits('search', (props.type === 'phone') ? `+7${phoneValue.unmasked}` : value.value)
  emits('search', props.modelValue);
};

const handleFocusSearchResults = (): void => {
  searchResultsElement.value?.focus();
};

const handleTogglePassword = (): void => {
  if (!inputValue.value) return;

  if (passwordInputType.value === 'password') {
    passwordInputType.value = 'text';
  } else {
    passwordInputType.value = 'password';
  }
};
// [END] Methods

// [START] Directive methods
const handleClickOutside = [
  () => {
    showSearchResults.value = false;
  },
  {
    ignore: ['contol'],
  },
] as any;
// [END] Directive methods

// Hooks

// onMounted(() => {
//   mask = new Mask(vMaskaOptions.value);
// })

return (_ctx: any,_cache: any) => {
  return _withDirectives((_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["input", classList.value]),
    ref_key: "root",
    ref: root
  }, [
    _createElementVNode("div", _hoisted_1, [
      _withDirectives(_createElementVNode("input", {
        ref_key: "input",
        ref: input,
        type: nativeType.value,
        value: inputValue.value,
        name: name.value,
        required: props.required,
        autocomplete: props.autocomplete,
        readonly: props.readonly,
        step: props.step,
        min: props.min,
        max: props.max,
        placeholder: props.placeholder,
        tabIndex: props.tabIndex,
        disabled: props.disabled,
        class: "input__control",
        onInput: _cache[0] || (_cache[0] = ($event: any) => (handleInput($event as InputEvent))),
        onKeydown: _withKeys(handleFocusSearchResults, ["down"]),
        onKeyup: _withKeys(handleSearch, ["enter"])
      }, null, 40, _hoisted_2), [
        [_unref(vMaska), void 0, vMaskaOptions.value]
      ]),
      (props.label)
        ? (_openBlock(), _createElementBlock("label", {
            key: 0,
            for: id.value,
            class: "input__label",
            innerHTML: props.label,
            onClick: handleFocus
          }, null, 8, _hoisted_3))
        : _createCommentVNode("", true),
      (shouldShowCancelIcon.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
            _createVNode(SmIcon, {
              name: "Cross",
              size: "18",
              class: "input__icon",
              onClick: handleCancel
            })
          ]))
        : _createCommentVNode("", true),
      (props.search)
        ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
            _createVNode(SmIcon, {
              name: "Loop",
              size: "18",
              class: "input__icon",
              onClick: handleSearch
            })
          ]))
        : _createCommentVNode("", true),
      (props.type === 'password')
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            _createVNode(SmIcon, {
              name: passwordIconName.value,
              class: _normalizeClass(["input__icon input__icon--pasword", passwordIconClassList.value]),
              onClick: handleTogglePassword
            }, null, 8, ["name", "class"])
          ]))
        : _createCommentVNode("", true),
      _withDirectives(_createElementVNode("div", _hoisted_7, [
        _createElementVNode("ul", {
          class: "search-results",
          ref_key: "searchResultsElement",
          ref: searchResultsElement,
          tabindex: "0"
        }, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(props.searchResults, (item, i) => {
            return (_openBlock(), _createElementBlock("li", {
              key: `search-result-item-${i}`,
              role: "menuitem",
              tabindex: 0,
              class: "search-results__item"
            }, [
              _renderSlot(_ctx.$slots, "searchItem", { item: item }, () => [
                _createElementVNode("p", _hoisted_8, _toDisplayString(item.title), 1),
                _createElementVNode("p", _hoisted_9, _toDisplayString(item.subtitle), 1)
              ])
            ]))
          }), 128))
        ], 512)
      ], 512), [
        [_vShow, shouldShowSearchIcon.value]
      ])
    ]),
    (shouldShowHint.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_10, _toDisplayString(props.hint), 1))
      : _createCommentVNode("", true),
    (props.error)
      ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(props.error), 1))
      : _createCommentVNode("", true)
  ], 2)), [
    [_unref(vOnClickOutside), handleClickOutside]
  ])
}
}

})